// External library imports
import React, { memo, useMemo, FC } from 'react';
import clsx from 'clsx';

// Internal library imports
import { Points, RichText } from '@marriott/mi-ui-library';

// Local imports
import { StyledPointsCard } from './PointsCard.styles';
import { PointsCardProps, VariationType } from './PointsCard.types';
import { POINTS_EARNED_CHARACTER_COUNT as charCount, COBRAND_CARD_TYPE } from '../../modules/constants/constants';
import { shouldCardDisplay } from '../../modules/utils';
import { useStore } from '../../modules/store/cobrandStore';

/**
 * PointsCard - Molecule component for displaying points information
 *
 * Features:
 * - Memoized for performance optimization
 * - Responsive layout with mobile/desktop variations
 * - Dynamic styling based on card type and variation
 * - Accessible with proper ARIA attributes
 *
 * Performance optimizations:
 * - React.memo prevents unnecessary re-renders
 * - useMemo for computed styles
 * - Early returns for edge cases
 *
 * @param props - PointsCardProps
 */
export const PointsCard: FC<PointsCardProps> = memo<PointsCardProps>(props => {
  const {
    number,
    content,
    isOnSpend,
    cardType,
    column,
    customClass = '',
    styleClass = '',
    bonusLabel,
    pointsForCarddetails = false,
    variationType = VariationType.Default,
    isBonusPointsLabelVisible = false,
    layoutType,
  } = props;

  // Get cobrand store data
  const { cobrandData } = useStore(store => store);

  // Memoize computed class names for performance
  const pointsTextAlignClass = useMemo(
    () => clsx('d-flex align-items-center', pointsForCarddetails ? 'pt-md-0' : 'pt-md-1'),
    [pointsForCarddetails]
  );

  const mobilePointsAlign = useMemo(
    () =>
      clsx(
        'd-flex',
        pointsForCarddetails
          ? 'flex-row points-card-detail align-items-start mb-2'
          : `flex-column flex-md-row ${
              variationType === VariationType.Default
                ? 'align-items-center pt-md-3 pt-0 pb-md-3'
                : 'text-center text-md-left'
            }`
      ),
    [pointsForCarddetails, variationType]
  );

  const pointSubtitleAlign = useMemo(
    () =>
      clsx(
        pointsForCarddetails
          ? ''
          : `${
              variationType === VariationType.Default ? 'mb-4 mb-md-0 mt-3 text-center text-md-left' : 'mt-2'
            } mt-md-0 ml-0 ml-md-3`
      ),
    [pointsForCarddetails, variationType]
  );

  /**
   * Get heading wrapper class based on card configuration
   */
  const getHeadingWrapperClass = useMemo(() => {
    if (!isOnSpend) {
      return 'pt-lg-2 d-block';
    }
    return mobilePointsAlign;
  }, [isOnSpend, mobilePointsAlign]);

  /**
   * Get subtitle class based on spend type
   */
  const getSubtitleClass = useMemo(
    () => (isOnSpend ? pointSubtitleAlign : 'py-2 py-md-3'),
    [isOnSpend, pointSubtitleAlign]
  );

  /**
   * Get content alignment class based on column and content length
   * Aligns points description with points number for short content
   */
  const getContentAlignmentClass = useMemo(() => {
    if (!isOnSpend) return '';

    const contentLength = content?.length || 0;

    // Check if content is short enough to align
    if (column === '3' && contentLength < charCount.THREE_COLUMN) {
      return 'd-flex align-items-center';
    }
    if (column === '2' && contentLength < charCount.TWO_COLUMN) {
      return 'd-flex align-items-center';
    }

    return pointsTextAlignClass;
  }, [isOnSpend, column, content, pointsTextAlignClass]);

  /**
   * Determine border style based on variation and context
   * Check pointsForCarddetails first to ensure card details section has no border
   */
  const borderStyle = useMemo(() => {
    if (pointsForCarddetails) return 'none';
    if (variationType === VariationType.Default) return 'rounded';
    return 'underlined';
  }, [variationType, pointsForCarddetails]);

  /**
   * Compute container style for business cards
   */
  const containerStyle = useMemo(() => {
    const isBusinessActive =
      cobrandData.activeTabValue === 'business' && shouldCardDisplay(cardType, cobrandData.activeTabValue);

    return isBusinessActive ? { minHeight: 0 } : {};
  }, [cobrandData.activeTabValue, cardType]);

  // Early return if no content
  if (!number || !content) {
    return null;
  }

  return (
    <StyledPointsCard
      data-component-name="m-cobrand-PointsCard"
      data-testid="cobrand-PointsCard"
      className={clsx(!isOnSpend && `points-${customClass}`, styleClass)}
      style={containerStyle}
      role="region"
      aria-label={layoutType !== 'enhanced' ? `Points card: ${number} points` : undefined}
    >
      <div className={getHeadingWrapperClass}>
        {/* Points number display */}
        <div className={clsx(!isOnSpend && 'mr-lg-0 mr-md-4', pointsForCarddetails && 'mr-2')}>
          <Points
            text={number}
            bonusLabel={bonusLabel}
            variant={pointsForCarddetails ? 'carddetails' : 'default'}
            borderstyle={borderStyle}
            isBonusPointsLabelVisible={isBonusPointsLabelVisible}
          />
        </div>

        {/* Points description text */}
        <RichText
          text={content}
          componentId="points-text"
          customClass={clsx(
            getSubtitleClass,
            getContentAlignmentClass,
            'align-items-center t-font-s points-description',
            pointsForCarddetails && 'points-card-detail-description'
          )}
        />
      </div>
    </StyledPointsCard>
  );
});

// Display name for debugging
PointsCard.displayName = 'PointsCard';
